import React from 'react';
import {
  Box,
  Text,
  Flex,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Textarea,
  useToast,
  TableContainer,
} from '@chakra-ui/react';
import qs from 'qs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import xhr from 'xhr';
import useAuth from 'hooks/useAuth';
import BasicModal from 'components/modal/Modal';
import { FaPaperPlane } from 'react-icons/fa';

dayjs.extend(relativeTime);
dayjs.locale('es');

async function fetchSchoolConversations(schoolId) {
  // 1. Get students of this school
  const studentsQuery = qs.stringify(
    {
      filters: { schoolFrom: { id: { $eq: schoolId } } },
      pagination: { limit: -1 },
      fields: ['id'],
      populate: { user: { fields: ['id', 'first_name', 'last_name'] } },
    },
    { encodeValuesOnly: true }
  );
  const studentsRes = await xhr(`/students?${studentsQuery}`);
  const students = studentsRes?.data?.data ?? studentsRes?.data ?? [];

  if (students.length === 0) return [];

  const studentUserIds = students
    .map((s) => s.user?.id ?? s.user)
    .filter(Boolean);

  if (studentUserIds.length === 0) return [];

  // Build student user map
  const studentUserMap = {};
  students.forEach((s) => {
    const uid = s.user?.id ?? s.user;
    if (uid) {
      studentUserMap[uid] = {
        studentId: s.id,
        first_name: s.user?.first_name || '',
        last_name: s.user?.last_name || '',
      };
    }
  });

  // 2. Get chats where school students are members (only members, no messages)
  const chatsQuery = qs.stringify(
    {
      filters: { members: { id: { $in: studentUserIds } } },
      pagination: { limit: -1 },
      sort: ['updatedAt:desc'],
      populate: {
        members: { fields: ['id', 'first_name', 'last_name'] },
      },
    },
    { encodeValuesOnly: true }
  );
  const chatsRes = await xhr(`/chats?${chatsQuery}`);
  const chats = chatsRes?.data?.data ?? chatsRes?.data ?? [];

  if (chats.length === 0) return [];

  // 3. Get last message of each chat in a single batch
  const chatIds = chats.map((c) => c.id);
  const msgsQuery = qs.stringify(
    {
      filters: { chat: { id: { $in: chatIds } } },
      sort: ['createdAt:desc'],
      pagination: { limit: -1 },
      fields: ['text', 'createdAt', 'seen'],
      populate: {
        user: { fields: ['id', 'first_name'] },
        chat: { fields: ['id'] },
      },
    },
    { encodeValuesOnly: true }
  );
  const msgsRes = await xhr(`/chat-messages?${msgsQuery}`);
  const allMsgs = msgsRes?.data?.data ?? msgsRes?.data ?? [];

  // Group: only keep the latest message per chat
  const lastMsgByChat = {};
  (Array.isArray(allMsgs) ? allMsgs : []).forEach((msg) => {
    const chatId = msg.chat?.id ?? msg.chat;
    if (chatId && !lastMsgByChat[chatId]) {
      lastMsgByChat[chatId] = msg;
    }
  });

  // 4. Build conversation list
  return chats
    .map((chat) => {
      const members = chat.members || [];
      const studentMember = members.find((m) => studentUserIds.includes(m.id));
      const otherMember = members.find((m) => !studentUserIds.includes(m.id));
      const lastMsg = lastMsgByChat[chat.id] || null;

      return {
        chatId: chat.id,
        student: studentMember
          ? {
              userId: studentMember.id,
              studentId: studentUserMap[studentMember.id]?.studentId,
              name: `${studentMember.first_name} ${studentMember.last_name}`,
            }
          : null,
        otherMember: otherMember
          ? { userId: otherMember.id, name: `${otherMember.first_name} ${otherMember.last_name}` }
          : null,
        lastMessage: lastMsg
          ? {
              text: lastMsg.text,
              createdAt: lastMsg.createdAt,
              senderUserId: lastMsg.user?.id,
              senderName: lastMsg.user?.first_name || '',
            }
          : null,
        updatedAt: chat.updatedAt,
      };
    })
    .filter((c) => c.student)
    .sort((a, b) => {
      const dateA = a.lastMessage?.createdAt || a.updatedAt;
      const dateB = b.lastMessage?.createdAt || b.updatedAt;
      return new Date(dateB) - new Date(dateA);
    });
}

async function fetchChatMessages(chatId) {
  const query = qs.stringify(
    {
      filters: { chat: { id: { $eq: chatId } } },
      pagination: { limit: -1 },
      sort: ['createdAt:asc'],
      populate: { user: { fields: ['id', 'first_name', 'last_name'] } },
    },
    { encodeValuesOnly: true }
  );
  const res = await xhr(`/chat-messages?${query}`);
  const list = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(list) ? list : [];
}

export default function SchoolMensajes() {
  const { user } = useAuth();
  const toast = useToast();

  const [conversations, setConversations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Detail modal
  const [isOpen, setIsOpen] = React.useState(false);
  const [currentChat, setCurrentChat] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [loadingMessages, setLoadingMessages] = React.useState(false);
  const [messageText, setMessageText] = React.useState('');
  const [sending, setSending] = React.useState(false);

  const messagesEndRef = React.useRef(null);

  React.useEffect(() => {
    const load = async () => {
      try {
        const schoolId = user?.school?.id;
        if (!schoolId) return;
        const list = await fetchSchoolConversations(schoolId);
        setConversations(list);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user?.school?.id]);

  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  const handleOpenChat = async (conv) => {
    setIsOpen(true);
    setCurrentChat(conv);
    setMessages([]);
    setMessageText('');
    setLoadingMessages(true);
    try {
      const msgs = await fetchChatMessages(conv.chatId);
      setMessages(msgs);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    try {
      if (!messageText.trim()) return;
      setSending(true);

      const res = await xhr.post('/chat-messages', {
        data: {
          chat: currentChat.chatId,
          text: messageText,
          user: user.id,
        },
      });

      const newMsg = res?.data?.data ?? res?.data;
      setMessages((prev) => [
        ...prev,
        { ...newMsg, user: { id: user.id, first_name: user.first_name, last_name: user.last_name } },
      ]);
      setMessageText('');

      toast({
        title: 'Mensaje enviado',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" mt={10}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box>
      <Flex align="center" gap={2} mb={4}>
        <Text fontSize="28px" fontWeight="bold">
          Mensajes de estudiantes
        </Text>
        <Text fontSize="28px" fontWeight="bold" color="red.500">
          ({conversations.length})
        </Text>
      </Flex>

      {conversations.length === 0 ? (
        <Text color="gray.500">No hay conversaciones de estudiantes.</Text>
      ) : (
        <TableContainer>
          <Table>
            <Thead bg="gray.200">
              <Tr>
                <Th>Estudiante</Th>
                <Th>Código</Th>
                <Th>Conversación con</Th>
                <Th>Último mensaje</Th>
                <Th>Fecha</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {conversations.map((conv) => (
                <Tr key={conv.chatId}>
                  <Td>{conv.student?.name}</Td>
                  <Td>{conv.student?.studentId}</Td>
                  <Td>{conv.otherMember?.name || '-'}</Td>
                  <Td maxW="250px">
                    {conv.lastMessage ? (
                      <Text fontSize="sm" color="gray.600" isTruncated>
                        {conv.lastMessage.senderName}: {conv.lastMessage.text}
                      </Text>
                    ) : (
                      <Text fontSize="sm" color="gray.400">Sin mensajes</Text>
                    )}
                  </Td>
                  <Td>
                    {conv.lastMessage
                      ? dayjs(conv.lastMessage.createdAt).fromNow()
                      : dayjs(conv.updatedAt).fromNow()}
                  </Td>
                  <Td>
                    <Button size="sm" colorScheme="purple" onClick={() => handleOpenChat(conv)}>
                      Ver
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Chat detail modal */}
      <BasicModal isOpen={isOpen} setIsOpen={setIsOpen} size="3xl">
        {currentChat && (
          <Box>
            <Flex justify="space-between" align="center" mb={3} pb={3} borderBottom="1px solid" borderColor="gray.200">
              <Box>
                <Text fontWeight="bold" fontSize="lg">
                  {currentChat.student?.name}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  Conversación con el Profesor
                </Text>
              </Box>
              <Text fontSize="sm" color="gray.500">
                Cod. {currentChat.student?.studentId}
              </Text>
            </Flex>

            {loadingMessages ? (
              <Flex justify="center" py={10}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <>
                {/* Messages */}
                <Box maxH="400px" overflowY="auto" mb={4} p={2}>
                  {messages.length === 0 ? (
                    <Text color="gray.400" textAlign="center" py={6}>No hay mensajes en esta conversación.</Text>
                  ) : (
                    messages.map((msg) => {
                      const isSchoolAdmin = msg.user?.id === user.id;
                      const isStudent = msg.user?.id === currentChat.student?.userId;
                      const senderLabel = isSchoolAdmin
                        ? 'Tú (Admin)'
                        : isStudent
                        ? currentChat.student?.name?.split(' ')[0]
                        : msg.user?.first_name || 'Profesor';

                      return (
                        <Box
                          key={msg.id}
                          mb={3}
                          ml={isSchoolAdmin ? '40px' : isStudent ? '0' : '20px'}
                          p={3}
                          bg={isSchoolAdmin ? 'purple.50' : isStudent ? 'gray.50' : 'blue.50'}
                          border="1px solid"
                          borderColor={isSchoolAdmin ? 'purple.200' : isStudent ? 'gray.200' : 'blue.200'}
                          borderRadius="md"
                        >
                          <Flex justify="space-between" align="center" mb={1}>
                            <Text fontWeight="bold" fontSize="sm">
                              {senderLabel}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {dayjs(msg.createdAt).format('D MMM YYYY HH:mm')}
                            </Text>
                          </Flex>
                          <Text fontSize="sm">{msg.text}</Text>
                        </Box>
                      );
                    })
                  )}
                  <Box ref={messagesEndRef} />
                </Box>

                {/* Reply */}
                <Flex gap={2} align="flex-end">
                  <Textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Escribe un mensaje..."
                    rows={2}
                    flex={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <Button
                    colorScheme="purple"
                    bg="#574feb"
                    onClick={handleSendMessage}
                    isLoading={sending}
                    disabled={!messageText.trim()}
                    leftIcon={<FaPaperPlane />}
                  >
                    Enviar
                  </Button>
                </Flex>
              </>
            )}
          </Box>
        )}
      </BasicModal>
    </Box>
  );
}
